import React, { useState, useEffect, useMemo } from 'react';
import './UserManagement.css';
import { useAuth } from './AuthContext';

interface User {
  id: number;
  username: string;
  name: string;
  password_hash: string;
  role: 'user' | 'admin',
  status: 'active' | 'inactive';
  joining_date?: string | null;
  wallet_balance?: number;
  mobile?: string | null;
  city?: string | null;
  account_number?: string | null;
  ifsc_code?: string | null;
  bank_name?: string | null;
  pan_number?: string | null;
}

type SortableUserKeys = 'id' | 'username' | 'password_hash' |'name' | 'mobile' | 'city' | 'wallet_balance' | 'role' | 'status' | 'joining_date' | 'account_number' | 'ifsc_code' | 'bank_name' | 'pan_number';

const SEARCHABLE_USER_KEYS: SortableUserKeys[] = [
  'id',
  'username',
  'name',
  'mobile',
  'city',
  'account_number',
  'ifsc_code',
  'bank_name',
  'pan_number',
];

interface SortConfig {
  key: SortableUserKeys;
  direction: 'ascending' | 'descending';
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [userToResetPassword, setUserToResetPassword] = useState<User | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({ key: 'id', direction: 'ascending' });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchableColumns, setSearchableColumns] = useState<Record<string, boolean>>({
    id: true,
    username: true,
    name: true,
    mobile: true,
    city: true,
    account_number: true,
    ifsc_code: true,
    bank_name: true,
    pan_number: true,
  });

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [goToPage, setGoToPage] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const { user: currentUser } = useAuth();
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    mobile: '',
    city: '',
    account_number: '',
    ifsc_code: '',
    bank_name: '',
    pan_number: '',
  });

  useEffect(() => {
    // All state that triggers a refetch is a dependency
    const fetchUsers = async () => {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('page', String(currentPage));
      params.append('limit', String(itemsPerPage));
      if (sortConfig) {
        params.append('sortKey', sortConfig.key);
        params.append('sortDirection', sortConfig.direction);
      }
      if (searchTerm) {
        params.append('searchTerm', searchTerm);
        const activeSearchCols = SEARCHABLE_USER_KEYS.filter(key => searchableColumns[key]);
        if (activeSearchCols.length > 0) {
          params.append('searchableColumns', activeSearchCols.join(','));
        }
      }
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      try {
        const response = await fetch(`/api/admin/users?${params.toString()}`, {
          credentials: 'include', // Sends cookies for authentication
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch users.');
        }

        const data = await response.json();
        setUsers(data.users);
        setTotalUsers(data.totalCount);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
        setError(errorMessage);
        console.error('Error fetching users:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [currentPage, itemsPerPage, sortConfig, searchTerm, searchableColumns, roleFilter, statusFilter, startDate, endDate]);

  const handleToggleStatus = async (user: User) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    // Clear previous errors when a new action starts
    setActionError(null);

    try {
      const response = await fetch(`/api/admin/users/${user.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || `Failed to ${newStatus} user.`);
      }

      // Update user in the list to reflect the change instantly
      setUsers(users.map(u => (u.id === user.id ? { ...u, status: newStatus } : u)));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setActionError(errorMessage);
    }
  };

  const handleSetPasswordClick = (user: User) => {
    setUserToResetPassword(user);
    setNewPassword(''); // Clear password field from previous use
    setActionError(null); // Clear previous errors
  };

  const handleCancelPasswordReset = () => {
    setUserToResetPassword(null);
  };

  const handleConfirmPasswordReset = async () => {
    if (!userToResetPassword) return;
    if (!newPassword) {
      setActionError('Password cannot be empty.');
      return;
    }

    try {
      const response = await fetch(`/api/admin/users/${userToResetPassword.id}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: newPassword }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to set new password.');
      }

      // Success, so close the modal
      setUserToResetPassword(null);
    } catch (err) {
      if (err instanceof Error) {
        setActionError(err.message);
      } else {
        setActionError('An unknown error occurred.');
      }
    }
  };

  const handleEditClick = (user: User) => {
    setEditingUserId(user.id);
    setEditFormData({
      name: user.name || '',
      mobile: user.mobile || '',
      city: user.city || '',
      account_number: user.account_number || '',
      ifsc_code: user.ifsc_code || '',
      bank_name: user.bank_name || '',
      pan_number: user.pan_number || '',
    });
    setActionError(null); // Clear previous errors
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setActionError(null);
  };

  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Automatically convert PAN and IFSC to uppercase
    if (name === 'pan_number' || name === 'ifsc_code') {
      setEditFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
      return;
    }
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async (userId: number) => {
    setActionError(null);

    // Client-side validation mirroring admin.user.routes.ts
    if (!editFormData.name || editFormData.name.trim() === '') {
      setActionError('Name is required and cannot be empty.');
      return;
    }
    if (editFormData.name.length > 50) {
      setActionError("Name cannot be longer than 50 characters.");
      return;
    }
    if (editFormData.city && editFormData.city.length > 30) {
      setActionError("City cannot be longer than 30 characters.");
      return;
    }
    if (editFormData.mobile && !/^\d{10}$/.test(editFormData.mobile)) {
      setActionError("Mobile number must be 10 digits.");
      return;
    }
    if (editFormData.account_number && !/^\d+$/.test(editFormData.account_number)) {
      setActionError("Account number must only contain digits.");
      return;
    }
    if (editFormData.account_number && editFormData.account_number.length > 18) {
      setActionError("Account number cannot be longer than 18 characters.");
      return;
    }
    if (editFormData.ifsc_code && !/^[A-Z0-9]{1,15}$/.test(editFormData.ifsc_code)) {
      setActionError("IFSC code should only contain up to 15 uppercase letters and numbers.");
      return;
    }
    if (editFormData.bank_name && editFormData.bank_name.length > 50) {
      setActionError("Bank name cannot be longer than 50 characters.");
      return;
    }
    if (editFormData.pan_number && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(editFormData.pan_number)) {
      setActionError("Please enter a valid PAN number format (e.g., ABCDE1234F).");
      return;
    }


    try {
      const response = await fetch(`/api/admin/users/${userId}/details`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editFormData),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update user details.');
      }

      // Update user in the list to reflect the change instantly
      setUsers(users.map(u => (u.id === userId ? { ...u, ...editFormData } : u)));
      setEditingUserId(null); // Exit edit mode
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setActionError(errorMessage);
    }
  };

  const isAllSearchable = React.useMemo(() => {
    return SEARCHABLE_USER_KEYS.every(key => searchableColumns[key]);
  }, [searchableColumns]);

  const isSomeSearchable = React.useMemo(() => {
    return SEARCHABLE_USER_KEYS.some(key => searchableColumns[key]) && !isAllSearchable;
  }, [searchableColumns, isAllSearchable]);

  const handleSelectAllSearchableChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    const newSearchableColumns = Object.fromEntries(
      SEARCHABLE_USER_KEYS.map(key => [key, isChecked])
    );
    setSearchableColumns(newSearchableColumns as typeof searchableColumns);
  };

  const handleSearchableColumnChange = (key: keyof typeof searchableColumns) => {
    setSearchableColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const requestSort = (key: SortableUserKeys) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (name: SortableUserKeys) => {
    if (!sortConfig || sortConfig.key !== name) {
      return <span className="sort-indicator" />;
    }
    return <span className="sort-indicator">{sortConfig.direction === 'ascending' ? '▲' : '▼'}</span>;
  };

  // Reset visible count when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, statusFilter, startDate, endDate, itemsPerPage]);

  const totalPages = itemsPerPage > 0 ? Math.ceil(totalUsers / itemsPerPage) : 1;
  const displayedUsers = users;

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleGoToPageSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const page = parseInt(goToPage, 10);
    if (!isNaN(page)) {
      handlePageChange(page);
    }
    setGoToPage('');
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  if (isLoading) return <div className="user-management-container"><h2>User Management</h2><p>Loading...</p></div>;
  if (error) return <div className="user-management-container"><h2>User Management</h2><div className="error-message">{error}</div></div>;

  return (
    <div className="user-management-container">
      <style>{`
        .action-button.recalculate-button {
          background-color: #17a2b8; /* A distinct teal color */
        }
        @media (max-width: 768px) {
          .user-table { border: 0; }
          .user-table thead {
            border: none;
            clip: rect(0 0 0 0);
            height: 1px;
            margin: -1px;
            overflow: hidden;
            padding: 0;
            position: absolute;
            width: 1px;
          }
          .toolbar, .filter-group {
            flex-direction: column;
            align-items: stretch;
          }
          .user-table tr {
            border-bottom: 3px solid #ddd;
            display: block;
            margin-bottom: 1em;
            padding-top: 1em; /* Add space between cards */
          }
          .user-table td {
            border-bottom: 1px solid #ddd;
            display: flex; /* Use flexbox for better alignment */
            justify-content: space-between; /* Align label and value */
            align-items: center;
            font-size: .8em;
            text-align: right; /* Keep text aligned to the right */
            padding: .8em; /* Add some padding */
          }
          .user-table td::before {
            content: attr(data-label);
            font-weight: bold;
            text-transform: uppercase;
            text-align: left; /* Align label to the left */
            margin-right: 10px; /* Space between label and value */
          }
          /* Target the content inside the td, not the td itself */
          .user-table td > * {
            word-break: break-all; /* Break long words/numbers */
          }
        }
        /* Mobile Card Specific Styles */
        .user-card {
          background-color: #fff;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 15px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
        .user-card .card-body {
          display: flex;
          flex-direction: column;
          gap: 8px; /* Adds space between each detail item */
        }
        .user-card .card-body p {
          margin: 0;
          font-size: 0.9rem; /* Slightly larger font for readability */
          word-break: break-word; /* Ensures long strings wrap */
        }
        .table-responsive-wrapper {
          overflow-x: auto; /* Allows table to scroll horizontally on smaller desktop screens */
          width: 100%;
        }
      `}</style>

      <h2>User Management</h2>
      {actionError && !userToResetPassword && <div className="error-message">{actionError}</div>}
      <div className="toolbar">
        <form onSubmit={handleSearchSubmit} className="search-form user-management-search">
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Search in selected columns..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="search-input"
            />
            {searchInput && (
              <button type="button" onClick={handleClearSearch} className="clear-search-button">&times;</button>
            )}
          </div>
          <button type="submit" className="search-button">
            Search
          </button>
        </form>
        <div className="filter-group">
          <label htmlFor="roleFilter">Filter by Role:</label>
          <select
            id="roleFilter"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="statusFilter">Filter by Status:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="startDate">Joined From:</label>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="date-input"
          />
          <label htmlFor="endDate">To:</label>
          <input
            type="date"
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="date-input"
          />
        </div>
      </div>

      {userToResetPassword && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Set New Password</h3>
            <p>Set a new password for user "<strong>{userToResetPassword.username}</strong>".</p>
            <div className="modal-form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="text"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="modal-input"
                autoFocus
              />
            </div>
            {actionError && <div className="error-message modal-error">{actionError}</div>}
            <div className="modal-actions">
              <button onClick={handleCancelPasswordReset} className="action-button cancel-button">Cancel</button>
              <button onClick={handleConfirmPasswordReset} className="action-button save-button">
                Save Password
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="table-responsive-wrapper"><table className="user-table">
        <thead>
          <tr>
            <th className="sno-column">
              <div className="th-content">
                <span className="header-text">S.No.</span>
                <label className="header-search-label" title="Toggle all columns for search">
                  <input
                    type="checkbox"
                    checked={isAllSearchable}
                    ref={el => {
                      if (el) el.indeterminate = isSomeSearchable;
                    }}
                    onChange={handleSelectAllSearchableChange}
                  />
                  All
                </label>
              </div>
            </th>
            <th onClick={() => requestSort('id')} className="sortable-header">
              <div className="th-content">
                <span className="header-text">ID {getSortIndicator('id')}</span>
                <label className="header-search-label">
                  <input
                    type="checkbox"
                    checked={searchableColumns.id}
                    onChange={() => handleSearchableColumnChange('id')}
                    onClick={(e) => e.stopPropagation()}
                  />
                  Search
                </label>
              </div>
            </th>
            <th onClick={() => requestSort('username')} className="sortable-header">
              <div className="th-content">
                <span className="header-text">Username {getSortIndicator('username')}</span>
                <label className="header-search-label">
                  <input
                    type="checkbox"
                    checked={searchableColumns.username}
                    onChange={() => handleSearchableColumnChange('username')}
                    onClick={(e) => e.stopPropagation()}
                  />
                  Search
                </label>
              </div>
            </th>
             <th onClick={() => requestSort('password_hash')} className="sortable-header password_hash">
              <div className="th-content">
                <span className="header-text">password {getSortIndicator('password_hash')}</span>
              </div>
            </th>
            <th onClick={() => requestSort('name')} className="sortable-header">
              <div className="th-content">
                <span className="header-text">Name {getSortIndicator('name')}</span>
                <label className="header-search-label">
                  <input
                    type="checkbox"
                    checked={searchableColumns.name}
                    onChange={() => handleSearchableColumnChange('name')}
                    onClick={(e) => e.stopPropagation()}
                  />
                  Search
                </label>
              </div>
            </th>
            <th onClick={() => requestSort('mobile')} className="sortable-header">
              <div className="th-content">
                <span className="header-text">Mobile {getSortIndicator('mobile')}</span>
                <label className="header-search-label">
                  <input
                    type="checkbox"
                    checked={searchableColumns.mobile}
                    onChange={() => handleSearchableColumnChange('mobile')}
                    onClick={(e) => e.stopPropagation()}
                  />
                  Search
                </label>
              </div>
            </th>
            <th onClick={() => requestSort('city')} className="sortable-header">
              <div className="th-content">
                <span className="header-text">City {getSortIndicator('city')}</span>
                <label className="header-search-label">
                  <input
                    type="checkbox"
                    checked={searchableColumns.city}
                    onChange={() => handleSearchableColumnChange('city')}
                    onClick={(e) => e.stopPropagation()}
                  />
                  Search
                </label>
              </div>
            </th>
            <th onClick={() => requestSort('account_number')} className="sortable-header">
              <div className="th-content">
                <span className="header-text">Account Number {getSortIndicator('account_number')}</span>
                <label className="header-search-label">
                  <input
                    type="checkbox"
                    checked={searchableColumns.account_number}
                    onChange={() => handleSearchableColumnChange('account_number')}
                    onClick={(e) => e.stopPropagation()}
                  />
                  Search
                </label>
              </div>
            </th>
            <th onClick={() => requestSort('ifsc_code')} className="sortable-header">
              <div className="th-content">
                <span className="header-text">IFSC Code {getSortIndicator('ifsc_code')}</span>
                <label className="header-search-label">
                  <input
                    type="checkbox"
                    checked={searchableColumns.ifsc_code}
                    onChange={() => handleSearchableColumnChange('ifsc_code')}
                    onClick={(e) => e.stopPropagation()}
                  />
                  Search
                </label>
              </div>
            </th>
            <th onClick={() => requestSort('bank_name')} className="sortable-header">
              <div className="th-content">
                <span className="header-text">Bank Name {getSortIndicator('bank_name')}</span>
                <label className="header-search-label">
                  <input type="checkbox" checked={searchableColumns.bank_name} onChange={() => handleSearchableColumnChange('bank_name')} onClick={(e) => e.stopPropagation()} />
                  Search
                </label>
              </div>
            </th>
            <th onClick={() => requestSort('pan_number')} className="sortable-header">
              <div className="th-content">
                <span className="header-text">PAN Number {getSortIndicator('pan_number')}</span>
                <label className="header-search-label">
                  <input type="checkbox" checked={searchableColumns.pan_number} onChange={() => handleSearchableColumnChange('pan_number')} onClick={(e) => e.stopPropagation()} />
                  Search
                </label>
              </div>
            </th>
            <th onClick={() => requestSort('wallet_balance')} className="sortable-header wallet-column">
              <div className="th-content">
                <span className="header-text">Wallet {getSortIndicator('wallet_balance')}</span>
              </div>
            </th>
            <th onClick={() => requestSort('role')} className="sortable-header">
              <div className="th-content">
                <span className="header-text">Role {getSortIndicator('role')}</span>
              </div>
            </th>
            <th onClick={() => requestSort('status')} className="sortable-header">
              <div className="th-content">
                <span className="header-text">Status {getSortIndicator('status')}</span>
              </div>
            </th>
            <th onClick={() => requestSort('joining_date')} className="sortable-header">
              <div className="th-content">
                <span className="header-text">Joining Date {getSortIndicator('joining_date')}</span>
              </div>
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {displayedUsers.length > 0 ? (displayedUsers.map((user, index) => (
            <tr key={user.id}>
              <td data-label="S.No." className="sno-column">{(currentPage - 1) * itemsPerPage + index + 1}</td>
              <td data-label="ID">{user.id}</td>
              <td data-label="Username">{user.username}</td>
              <td data-label="Password">{user.password_hash}</td>
              {editingUserId === user.id ? (
                <> {/* Inputs with validation attributes */}
                  <td data-label="Name"><input type="text" name="name" value={editFormData.name} onChange={handleEditFormChange} className="edit-input" maxLength={50} required /></td>
                  <td data-label="Mobile"><input type="tel" name="mobile" value={editFormData.mobile} onChange={handleEditFormChange} className="edit-input" maxLength={10} pattern="\d{10}" title="Mobile must be 10 digits." /></td>
                  <td data-label="City"><input type="text" name="city" value={editFormData.city} onChange={handleEditFormChange} className="edit-input" maxLength={30} /></td>
                  <td data-label="Account Number"><input type="text" name="account_number" value={editFormData.account_number} onChange={handleEditFormChange} className="edit-input" maxLength={18} pattern="\d*" title="Account number must be digits only." /></td>
                  <td data-label="IFSC Code"><input type="text" name="ifsc_code" value={editFormData.ifsc_code} onChange={handleEditFormChange} className="edit-input" maxLength={15} style={{ textTransform: 'uppercase' }} /></td>
                  <td data-label="Bank Name"><input type="text" name="bank_name" value={editFormData.bank_name} onChange={handleEditFormChange} className="edit-input" maxLength={50} /></td>
                  <td data-label="PAN Number"><input type="text" name="pan_number" value={editFormData.pan_number} onChange={handleEditFormChange} className="edit-input" maxLength={10} pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}" title="PAN must be in the format ABCDE1234F." style={{ textTransform: 'uppercase' }} /></td>
                </>
              ) : (
                <>
                  <td data-label="Name">{user.name || 'N/A'}</td>
                  <td data-label="Mobile">{user.mobile || 'N/A'}</td>
                  <td data-label="City">{user.city || 'N/A'}</td>
                  <td data-label="Account Number">{user.account_number || 'N/A'}</td>
                  <td data-label="IFSC Code">{user.ifsc_code || 'N/A'}</td>
                  <td data-label="Bank Name">{user.bank_name || 'N/A'}</td>
                  <td data-label="PAN Number">{user.pan_number || 'N/A'}</td>
                </>
              )}
              <td data-label="Wallet" className="wallet-column">
                {user.wallet_balance != null ? `₹${user.wallet_balance.toFixed(2)}` : 'N/A'}
              </td>
              <td data-label="Role">{user.role}</td>
              <td data-label="Status">
                <span className={`status-badge status-${user.status}`}>{user.status}</span>
              </td>
              <td data-label="Joining Date">
                {user.joining_date ? new Date(user.joining_date).toLocaleDateString() : 'N/A'}
              </td>
              <td data-label="Actions">
                {editingUserId === user.id ? (
                  <>
                    <button onClick={() => handleSaveEdit(user.id)} className="action-button save-button">Save</button>
                    <button onClick={handleCancelEdit} className="action-button cancel-button">Cancel</button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleEditClick(user)}
                      className="action-button edit-button"
                      title="Edit user details"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleStatus(user)}
                      className={`action-button ${user.status === 'active' ? 'disable-button' : 'enable-button'}`}
                      disabled={user.id === currentUser?.id}
                      title={user.id === currentUser?.id ? "You cannot change your own status" : (user.status === 'active' ? 'Disable user' : 'Enable user')}
                    >
                      {user.status === 'active' ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      onClick={() => handleSetPasswordClick(user)}
                      className="action-button set-password-button"
                      title="Set new password"
                    >
                      Set Password
                    </button>
                    {/* The individual recalculate button is removed from here */}
                  </>
                )}
              </td>
            </tr>
          ))) : (
            <tr>
              <td colSpan={17} style={{ textAlign: 'center' }}>No users found.</td>
            </tr>
          )}
        </tbody>
      </table></div>
      {totalUsers > 0 && (
        <div className="pagination-controls">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
            &laquo; First
          </button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            &lsaquo; Previous
          </button>
          <span className="page-info">Page {currentPage} of {totalPages}</span>
          <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            Next &rsaquo;
          </button>
          <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
            Last &raquo;
          </button>
          <form onSubmit={handleGoToPageSubmit} className="go-to-page-form">
            <input
              type="number"
              value={goToPage}
              onChange={(e) => setGoToPage(e.target.value)}
              className="page-input"
              min="1"
              max={totalPages}
              placeholder="Page"
            />
            <button type="submit">Go</button>
          </form>
          <div className="items-per-page-selector">
            <span>Items per page:</span>
            <select value={itemsPerPage} onChange={e => setItemsPerPage(Number(e.target.value))} className="filter-select">
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
              <option value={totalUsers}>All</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;